import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Row, Col, Title, SubTitle, Text } from "../theme";
import { checkStoredOrPromptPassword } from "../utilities/passwordCookie";

const tableStyle = {
  borderCollapse: "collapse",
  color: "white",
  marginBottom: "30px",
  width: "100%",
};

const tableScrollStyle = {
  overflowX: "auto",
  width: "100%",
};

const cellStyle = {
  borderBottom: "1px solid rgba(255, 255, 255, 0.35)",
  padding: "8px",
  textAlign: "left",
};

const numberStyle = {
  ...cellStyle,
  textAlign: "right",
};

const compactCellStyle = {
  ...cellStyle,
  fontSize: "0.9em",
};

const compactNumberStyle = {
  ...compactCellStyle,
  textAlign: "right",
};

const expandButtonStyle = {
  background: "transparent",
  border: 0,
  color: "white",
  cursor: "pointer",
  font: "inherit",
  padding: 0,
  textAlign: "left",
  textDecoration: "underline",
};

const linkStyle = {
  color: "white",
};

const barTrackStyle = {
  background: "rgba(255, 255, 255, 0.16)",
  height: "14px",
  width: "100%",
};

const getBarStyle = (value, max) => ({
  background: "white",
  height: "14px",
  width: `${Math.round((value / max) * 100)}%`,
});

const SANITY_MEME_URL =
  "https://studio.massiveexoplanetmemeexhibition.com/desk/meme;";

const getMemeTitle = (meme) => meme.description || meme._id;

const getTagNames = (meme) =>
  meme.taxonomy?.map((tag) => tag.name).filter(Boolean) || [];

const getTagCount = (meme) => meme.taxonomy?.length || 0;

const formatPercent = (value) => `${Math.round(value * 100)}%`;

const getHeatCellStyle = (value, max) => {
  const opacity = max > 0 ? 0.12 + (value / max) * 0.55 : 0;

  return {
    ...compactNumberStyle,
    background: value > 0 ? `rgba(255, 255, 255, ${opacity})` : "transparent",
  };
};

const MemeList = ({ memes }) => {
  if (memes.length === 0) {
    return <Text>No memes found.</Text>;
  }

  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={cellStyle}>Meme</th>
          <th style={cellStyle}>Edition</th>
          <th style={numberStyle}>Links</th>
        </tr>
      </thead>
      <tbody>
        {memes.map((meme) => (
          <tr key={meme._id}>
            <td style={cellStyle}>{getMemeTitle(meme)}</td>
            <td style={cellStyle}>{meme.edition || "none"}</td>
            <td style={numberStyle}>
              {meme.img ? (
                <>
                  <a
                    href={`/gallery/${meme._id}`}
                    rel="noopener noreferrer"
                    style={linkStyle}
                    target="_blank"
                  >
                    image
                  </a>
                  {" | "}
                </>
              ) : null}
              <a
                href={`/tagger/${meme._id}`}
                rel="noopener noreferrer"
                style={linkStyle}
                target="_blank"
              >
                tagger
              </a>
              {" | "}
              <a
                href={`${SANITY_MEME_URL}${meme._id}`}
                rel="noopener noreferrer"
                style={linkStyle}
                target="_blank"
              >
                sanity
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const TaggerInsights = () => {
  const [settings, memes, tags, editions] = useSelector((state) => [
    state.data.settings,
    state.data.memes || [],
    state.data.tags || [],
    state.data.editions || [],
  ]);
  const [secure, setSecure] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (secure === null && settings) {
      setSecure(checkStoredOrPromptPassword(settings.password));
    }
  }, [secure, settings]);

  const tagNameById = useMemo(() => {
    const names = {};
    tags.forEach((tag) => {
      names[tag._id] = tag.name;
    });
    return names;
  }, [tags]);

  const categoryInsights = useMemo(() => {
    if (!settings?.selectedTagsGrouped) {
      return [];
    }

    return settings.selectedTagsGrouped.map((group) => {
      const subcategories = group.selectedTags
        .map(({ _ref }) => tagNameById[_ref])
        .filter(Boolean)
        .map((name) => {
          const matchingMemes = memes.filter((meme) =>
            meme.taxonomy?.some((tag) => tag.name === name)
          );

          return {
            name,
            count: matchingMemes.length,
            memes: matchingMemes,
          };
        });

      return {
        categoryTitle: group.categoryTitle,
        total: subcategories.reduce((sum, tag) => sum + tag.count, 0),
        subcategories,
      };
    });
  }, [settings, tagNameById, memes]);

  const categoryDefinitions = useMemo(
    () =>
      categoryInsights.map(({ categoryTitle, subcategories }) => ({
        categoryTitle,
        tagNames: subcategories.map(({ name }) => name),
      })),
    [categoryInsights]
  );

  const memeSummary = useMemo(
    () => ({
      noTags: memes.filter((meme) => (meme.taxonomy?.length || 0) === 0),
      noEdition: memes.filter((meme) => !meme.edition),
    }),
    [memes]
  );

  const editionNames = useMemo(() => {
    const names =
      editions.length > 0
        ? editions.map(({ edition }) => edition).filter(Boolean)
        : [...new Set(memes.map((meme) => meme.edition).filter(Boolean))];
    return names.sort((a, b) => Number(a) - Number(b));
  }, [editions, memes]);

  const editionBuckets = useMemo(() => {
    const buckets = editionNames.map((edition) => ({
      edition,
      label: edition,
      memes: memes.filter((meme) => meme.edition === edition),
    }));

    const noEditionMemes = memes.filter((meme) => !meme.edition);
    if (noEditionMemes.length > 0) {
      buckets.push({
        edition: null,
        label: "No edition",
        memes: noEditionMemes,
      });
    }

    return buckets;
  }, [editionNames, memes]);

  const editionInsights = useMemo(() => {
    return editionBuckets.map(({ label, memes: editionMemes }) => {
      const noTags = editionMemes.filter((meme) => getTagCount(meme) === 0);
      const lowTags = editionMemes.filter(
        (meme) => getTagCount(meme) > 0 && getTagCount(meme) < 3
      );
      const wellTagged = editionMemes.filter((meme) => getTagCount(meme) >= 3);
      const totalTags = editionMemes.reduce(
        (sum, meme) => sum + getTagCount(meme),
        0
      );

      return {
        edition: label,
        memeCount: editionMemes.length,
        noTags,
        lowTags,
        wellTagged,
        averageTags:
          editionMemes.length > 0 ? totalTags / editionMemes.length : 0,
      };
    });
  }, [editionBuckets]);

  const tagCountDistribution = useMemo(() => {
    const buckets = [
      { label: "0 tags", test: (count) => count === 0 },
      { label: "1 tag", test: (count) => count === 1 },
      { label: "2 tags", test: (count) => count === 2 },
      { label: "3 tags", test: (count) => count === 3 },
      { label: "4+ tags", test: (count) => count >= 4 },
    ];

    return buckets.map(({ label, test }) => ({
      label,
      memes: memes.filter((meme) => test(getTagCount(meme))),
    }));
  }, [memes]);

  const editionCategoryHeatmap = useMemo(
    () =>
      editionBuckets.map(({ label, memes: editionMemes }) => ({
        edition: label,
        categories: categoryDefinitions.map(({ categoryTitle, tagNames }) => {
          const tagSet = new Set(tagNames);
          const matchingMemes = editionMemes.filter((meme) =>
            getTagNames(meme).some((name) => tagSet.has(name))
          );

          return {
            categoryTitle,
            count: matchingMemes.length,
            memes: matchingMemes,
          };
        }),
      })),
    [editionBuckets, categoryDefinitions]
  );

  const missingCategoryMatrix = useMemo(
    () =>
      editionBuckets.map(({ label, memes: editionMemes }) => ({
        edition: label,
        categories: categoryDefinitions.map(({ categoryTitle, tagNames }) => {
          const tagSet = new Set(tagNames);
          const missingMemes = editionMemes.filter(
            (meme) => !getTagNames(meme).some((name) => tagSet.has(name))
          );

          return {
            categoryTitle,
            count: missingMemes.length,
            memes: missingMemes,
          };
        }),
      })),
    [editionBuckets, categoryDefinitions]
  );

  const coOccurrenceInsights = useMemo(() => {
    const pairs = {};

    memes.forEach((meme) => {
      const tagNames = [...new Set(getTagNames(meme))].sort();

      tagNames.forEach((firstTag, firstIndex) => {
        tagNames.slice(firstIndex + 1).forEach((secondTag) => {
          const key = `${firstTag}|||${secondTag}`;

          if (!pairs[key]) {
            pairs[key] = {
              firstTag,
              secondTag,
              memes: [],
            };
          }

          pairs[key].memes.push(meme);
        });
      });
    });

    return Object.values(pairs)
      .sort((a, b) => b.memes.length - a.memes.length)
      .slice(0, 20);
  }, [memes]);

  const maxDistributionCount = Math.max(
    1,
    ...tagCountDistribution.map(({ memes: bucketMemes }) => bucketMemes.length)
  );

  const maxEditionCategoryCount = Math.max(
    1,
    ...editionCategoryHeatmap.flatMap(({ categories }) =>
      categories.map(({ count }) => count)
    )
  );

  const maxMissingCategoryCount = Math.max(
    1,
    ...missingCategoryMatrix.flatMap(({ categories }) =>
      categories.map(({ count }) => count)
    )
  );

  const maxCoOccurrenceCount = Math.max(
    1,
    ...coOccurrenceInsights.map(({ memes: pairMemes }) => pairMemes.length)
  );

  const toggleExpanded = (key) => {
    setExpanded((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  if (!settings) {
    return null;
  }

  if (secure === false) {
    return (
      <Row center="xs">
        <Col xs={12}>
          <Title>TAGGER INSIGHTS</Title>
          <Text>Wrong password.</Text>
        </Col>
      </Row>
    );
  }

  if (!secure) {
    return null;
  }

  return (
    <>
      <Row center="xs">
        <Col xs={12}>
          <Title>TAGGER INSIGHTS</Title>
          <Text>
            <button
              onClick={() => toggleExpanded("summary-no-tags")}
              style={expandButtonStyle}
              type="button"
            >
              {expanded["summary-no-tags"] ? "Collapse" : "Expand"} memes with
              no tags: {memeSummary.noTags.length}
            </button>
          </Text>
          {expanded["summary-no-tags"] ? (
            <MemeList memes={memeSummary.noTags} />
          ) : null}
          <Text>
            <button
              onClick={() => toggleExpanded("summary-no-edition")}
              style={expandButtonStyle}
              type="button"
            >
              {expanded["summary-no-edition"] ? "Collapse" : "Expand"} memes
              with no edition: {memeSummary.noEdition.length}
            </button>
          </Text>
          {expanded["summary-no-edition"] ? (
            <MemeList memes={memeSummary.noEdition} />
          ) : null}
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <SubTitle>Tag Count Distribution</SubTitle>
          <div style={tableScrollStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={cellStyle}>Tags</th>
                  <th style={numberStyle}>Memes</th>
                  <th style={cellStyle}>Volume</th>
                </tr>
              </thead>
              <tbody>
                {tagCountDistribution.map(({ label, memes: bucketMemes }) => {
                  const expandedKey = `distribution-${label}`;

                  return (
                    <React.Fragment key={label}>
                      <tr>
                        <td style={cellStyle}>
                          <button
                            onClick={() => toggleExpanded(expandedKey)}
                            style={expandButtonStyle}
                            type="button"
                          >
                            {expanded[expandedKey] ? "Collapse" : "Expand"}{" "}
                            {label}
                          </button>
                        </td>
                        <td style={numberStyle}>{bucketMemes.length}</td>
                        <td style={cellStyle}>
                          <div style={barTrackStyle}>
                            <div
                              style={getBarStyle(
                                bucketMemes.length,
                                maxDistributionCount
                              )}
                            />
                          </div>
                        </td>
                      </tr>
                      {expanded[expandedKey] ? (
                        <tr>
                          <td colSpan={3} style={cellStyle}>
                            <MemeList memes={bucketMemes} />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <SubTitle>Edition Completeness</SubTitle>
          <div style={tableScrollStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={cellStyle}>Edition</th>
                  <th style={numberStyle}>Memes</th>
                  <th style={numberStyle}>No Tags</th>
                  <th style={numberStyle}>1-2 Tags</th>
                  <th style={numberStyle}>3+ Tags</th>
                  <th style={numberStyle}>Coverage</th>
                  <th style={numberStyle}>Average Tags</th>
                </tr>
              </thead>
              <tbody>
                {editionInsights.map(
                  ({
                    edition,
                    memeCount,
                    noTags,
                    lowTags,
                    wellTagged,
                    averageTags,
                  }) => {
                    const noTagsKey = `edition-${edition}-no-tags`;
                    const lowTagsKey = `edition-${edition}-low-tags`;
                    const wellTaggedKey = `edition-${edition}-well-tagged`;
                    const expandedRows = [
                      { key: noTagsKey, label: "No tags", memes: noTags },
                      { key: lowTagsKey, label: "1-2 tags", memes: lowTags },
                      {
                        key: wellTaggedKey,
                        label: "3+ tags",
                        memes: wellTagged,
                      },
                    ].filter(({ key }) => expanded[key]);

                    return (
                      <React.Fragment key={edition}>
                        <tr>
                          <td style={cellStyle}>{edition}</td>
                          <td style={numberStyle}>{memeCount}</td>
                          <td style={numberStyle}>
                            <button
                              onClick={() => toggleExpanded(noTagsKey)}
                              style={expandButtonStyle}
                              type="button"
                            >
                              {noTags.length}
                            </button>
                          </td>
                          <td style={numberStyle}>
                            <button
                              onClick={() => toggleExpanded(lowTagsKey)}
                              style={expandButtonStyle}
                              type="button"
                            >
                              {lowTags.length}
                            </button>
                          </td>
                          <td style={numberStyle}>
                            <button
                              onClick={() => toggleExpanded(wellTaggedKey)}
                              style={expandButtonStyle}
                              type="button"
                            >
                              {wellTagged.length}
                            </button>
                          </td>
                          <td style={numberStyle}>
                            {memeCount > 0
                              ? formatPercent(wellTagged.length / memeCount)
                              : "0%"}
                          </td>
                          <td style={numberStyle}>{averageTags.toFixed(2)}</td>
                        </tr>
                        {expandedRows.map(({ key, label, memes }) => (
                          <tr key={key}>
                            <td colSpan={7} style={cellStyle}>
                              <Text>
                                <strong>
                                  {edition}: {label}
                                </strong>
                              </Text>
                              <MemeList memes={memes} />
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>
        </Col>
      </Row>

      {categoryDefinitions.length > 0 ? (
        <>
          <Row>
            <Col xs={12}>
              <SubTitle>Edition x Category Heatmap</SubTitle>
              <div style={tableScrollStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={compactCellStyle}>Edition</th>
                      {categoryDefinitions.map(({ categoryTitle }) => (
                        <th key={categoryTitle} style={compactNumberStyle}>
                          {categoryTitle}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {editionCategoryHeatmap.map(({ edition, categories }) => (
                      <React.Fragment key={edition}>
                        <tr>
                          <td style={compactCellStyle}>{edition}</td>
                          {categories.map(({ categoryTitle, count }) => {
                            const expandedKey = `heat-${edition}-${categoryTitle}`;

                            return (
                              <td
                                key={categoryTitle}
                                style={getHeatCellStyle(
                                  count,
                                  maxEditionCategoryCount
                                )}
                              >
                                <button
                                  onClick={() => toggleExpanded(expandedKey)}
                                  style={expandButtonStyle}
                                  type="button"
                                >
                                  {count}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                        {categories
                          .filter(
                            ({ categoryTitle }) =>
                              expanded[`heat-${edition}-${categoryTitle}`]
                          )
                          .map(({ categoryTitle, memes }) => (
                            <tr key={`heat-${edition}-${categoryTitle}`}>
                              <td
                                colSpan={categoryDefinitions.length + 1}
                                style={cellStyle}
                              >
                                <Text>
                                  <strong>
                                    {edition}: {categoryTitle}
                                  </strong>
                                </Text>
                                <MemeList memes={memes} />
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <SubTitle>Missing Category Matrix</SubTitle>
              <div style={tableScrollStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={compactCellStyle}>Edition</th>
                      {categoryDefinitions.map(({ categoryTitle }) => (
                        <th key={categoryTitle} style={compactNumberStyle}>
                          {categoryTitle}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {missingCategoryMatrix.map(({ edition, categories }) => (
                      <React.Fragment key={edition}>
                        <tr>
                          <td style={compactCellStyle}>{edition}</td>
                          {categories.map(({ categoryTitle, count }) => {
                            const expandedKey = `missing-${edition}-${categoryTitle}`;

                            return (
                              <td
                                key={categoryTitle}
                                style={getHeatCellStyle(
                                  count,
                                  maxMissingCategoryCount
                                )}
                              >
                                <button
                                  onClick={() => toggleExpanded(expandedKey)}
                                  style={expandButtonStyle}
                                  type="button"
                                >
                                  {count}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                        {categories
                          .filter(
                            ({ categoryTitle }) =>
                              expanded[`missing-${edition}-${categoryTitle}`]
                          )
                          .map(({ categoryTitle, memes }) => (
                            <tr key={`missing-${edition}-${categoryTitle}`}>
                              <td
                                colSpan={categoryDefinitions.length + 1}
                                style={cellStyle}
                              >
                                <Text>
                                  <strong>
                                    {edition}: missing {categoryTitle}
                                  </strong>
                                </Text>
                                <MemeList memes={memes} />
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <SubTitle>Top Tag Co-Occurrences</SubTitle>
              <div style={tableScrollStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={cellStyle}>Tags</th>
                      <th style={numberStyle}>Memes</th>
                      <th style={cellStyle}>Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coOccurrenceInsights.map(
                      ({ firstTag, secondTag, memes: pairMemes }) => {
                        const label = `${firstTag} + ${secondTag}`;
                        const expandedKey = `pair-${label}`;

                        return (
                          <React.Fragment key={label}>
                            <tr>
                              <td style={cellStyle}>
                                <button
                                  onClick={() => toggleExpanded(expandedKey)}
                                  style={expandButtonStyle}
                                  type="button"
                                >
                                  {expanded[expandedKey]
                                    ? "Collapse"
                                    : "Expand"}{" "}
                                  {label}
                                </button>
                              </td>
                              <td style={numberStyle}>{pairMemes.length}</td>
                              <td style={cellStyle}>
                                <div style={barTrackStyle}>
                                  <div
                                    style={getBarStyle(
                                      pairMemes.length,
                                      maxCoOccurrenceCount
                                    )}
                                  />
                                </div>
                              </td>
                            </tr>
                            {expanded[expandedKey] ? (
                              <tr>
                                <td colSpan={3} style={cellStyle}>
                                  <MemeList memes={pairMemes} />
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
        </>
      ) : null}

      <Row>
        <Col xs={12}>
          <SubTitle>Categories</SubTitle>
          {categoryInsights.map(({ categoryTitle, total, subcategories }) => (
            <div key={categoryTitle}>
              <Text>
                <strong>
                  {categoryTitle} ({total})
                </strong>
              </Text>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={cellStyle}>Subcategory</th>
                    <th style={numberStyle}>Memes</th>
                  </tr>
                </thead>
                <tbody>
                  {subcategories.map(({ name, count, memes }) => (
                    <React.Fragment key={name}>
                      <tr>
                        <td style={cellStyle}>
                          <button
                            onClick={() =>
                              toggleExpanded(`${categoryTitle}-${name}`)
                            }
                            style={expandButtonStyle}
                            type="button"
                          >
                            {expanded[`${categoryTitle}-${name}`]
                              ? "Collapse"
                              : "Expand"}{" "}
                            {name}
                          </button>
                        </td>
                        <td style={numberStyle}>{count}</td>
                      </tr>
                      {expanded[`${categoryTitle}-${name}`] ? (
                        <tr>
                          <td colSpan={2} style={cellStyle}>
                            <MemeList memes={memes} />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <SubTitle>Average Tags Per Edition</SubTitle>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={cellStyle}>Edition</th>
                <th style={numberStyle}>Memes</th>
                <th style={numberStyle}>Average Tags</th>
              </tr>
            </thead>
            <tbody>
              {editionInsights.map(({ edition, memeCount, averageTags }) => (
                <tr key={edition}>
                  <td style={cellStyle}>{edition}</td>
                  <td style={numberStyle}>{memeCount}</td>
                  <td style={numberStyle}>{averageTags.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Col>
      </Row>
    </>
  );
};

export default TaggerInsights;
